import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { getCurrentSession, logout, isStudent, isTeacher } from './services/authService';
import AuthRouter from './components/Auth/AuthRouter';
import App from './App'; // V2 Quiz App (for students) - Keep static for now or lazy load if desired

// Lazy load heavy dashboard components
const Dashboard = React.lazy(() => import('./components/Teacher/Dashboard')); // V3 Teacher Dashboard
const StudentDashboardComponent = React.lazy(() => import('./components/Student/StudentDashboard')); // V3 Student Dashboard

/**
 * Fluence V3 - Main App with Authentication
 * Routes to AuthRouter (if not logged in) or Dashboard (if logged in)
 */

// Shared Loading Component
const GlobalLoader = () => (
  <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center">
    <div className="text-center">
      <div className="text-6xl mb-4 animate-bounce">🎓</div>
      <p className="text-gray-600 text-lg">Loading Fluence...</p>
    </div>
  </div>
);

function AppV3() {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const existingSession = getCurrentSession();
    if (existingSession) {
      setSession(existingSession);
    }
    setLoading(false);
  }, []);

  const handleAuthenticated = (newSession) => {
    setSession(newSession);
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
      setSession(null);
    }
  };

  // Loading state (Initial Session Check)
  if (loading) {
    return <GlobalLoader />;
  }

  // Wrap everything in BrowserRouter with basename for GitHub Pages
  return (
    <BrowserRouter basename={process.env.PUBLIC_URL}>
      <Suspense fallback={<GlobalLoader />}>
        <Routes>
          {/* Not authenticated - show auth screens */}
          {!session && (
            <Route path="*" element={<AuthRouter onAuthenticated={handleAuthenticated} />} />
          )}

          {/* Teacher Routes */}
          {session && isTeacher() && (
            <>
              <Route path="/" element={<Navigate to="/teacher/dashboard" replace />} />
              <Route path="/teacher/*" element={<Dashboard />} />
            </>
          )}

          {/* Student Routes (after teacher routes to prevent catching /teacher paths) */}
          {session && isStudent() && (
            <>
              <Route path="/" element={<StudentDashboard session={session} onLogout={handleLogout} />} />
              <Route path="/student/*" element={<StudentDashboard session={session} onLogout={handleLogout} />} />
            </>
          )}

          {/* Fallback */}
          <Route path="*" element={
            <div className="min-h-screen flex items-center justify-center">
              <p className="text-gray-600">Unknown user type. Please refresh and try again.</p>
            </div>
          } />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

/**
 * Student Dashboard Wrapper (V3 - WITH QUIZ INTEGRATION)
 * Handles toggling between dashboard view and quiz view
 */
const StudentDashboard = ({ session, onLogout }) => {
  const [showQuiz, setShowQuiz] = useState(false);

  // If quiz is active, show the quiz game
  if (showQuiz) {
    return <App session={session} onBack={() => setShowQuiz(false)} />;
  }

  // Otherwise show the new dashboard component
  return (
    <StudentDashboardComponent
      session={session}
      onLogout={onLogout}
      onStartQuiz={() => setShowQuiz(true)}
    />
  );
};

export default AppV3;
